/**
 * #198: the user menu's "Concept guide" entry — a deep link to the Library document that holds
 * the shared vocabulary (docs/CONCEPT-GUIDE.md, seeded as a Spec document). The document is data,
 * so it is found at click time by a registry search rather than compiled in: by its key when one
 * is configured, otherwise by its title. When the Library has no such document the link falls
 * back to the Library itself, never to a dead page.
 */
import { apiFetch } from '@/api/client';
import { endpoints } from '@/api/endpoints';
import { normalizeList } from '@/api/normalize';
import { entityHref } from '@/lib/entity-links';

export const CONCEPT_GUIDE_TITLE = 'Concept guide';
export const CONCEPT_GUIDE_DESCRIPTION = 'What each thing is and how they connect — the shared vocabulary';
/** The seeded document's stable key, when known; the title match is the fallback. */
export const CONCEPT_GUIDE_KEY: string | null = 'DOC-CONCEPT-GUIDE';
export const LIBRARY_HREF = '/library';

interface DocumentRow {
  id: string;
  key?: string | null;
  name?: string | null;
}

/** Where the entry opens: the guide's own Library page, or the Library when it is absent. */
export function conceptGuideHref(rows: readonly DocumentRow[], key: string | null = CONCEPT_GUIDE_KEY): string {
  const byKey = key ? rows.find((r) => r.key === key) : undefined;
  const byTitle = rows.find((r) => r.name?.trim().toLowerCase().endsWith(CONCEPT_GUIDE_TITLE.toLowerCase()));
  const hit = byKey ?? byTitle;
  return (hit && entityHref({ type: 'document', id: hit.id, key: hit.key })) || LIBRARY_HREF;
}

async function searchDocuments(q: string): Promise<DocumentRow[]> {
  const list = normalizeList(await apiFetch(`${endpoints.entities}?type=document&q=${encodeURIComponent(q)}&pageSize=20`));
  return list.rows as DocumentRow[];
}

/** By key first; when the key finds nothing, by title — then the Library itself. */
export async function resolveConceptGuideHref(): Promise<string> {
  try {
    if (CONCEPT_GUIDE_KEY) {
      const href = conceptGuideHref(await searchDocuments(CONCEPT_GUIDE_KEY));
      if (href !== LIBRARY_HREF) return href;
    }
    return conceptGuideHref(await searchDocuments(CONCEPT_GUIDE_TITLE));
  } catch {
    return LIBRARY_HREF;
  }
}
